import escapeHTML from 'escape-html'
import { useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { NotificationController } from 'core-components/library/popup'
import {
  createSitePage,
  getSortedChildren,
} from 'core-components/sitebuilder/layout/helpers/sitePages'
import { ISitePage } from 'core-components/sitebuilder/models/ISitePage'
import { IWidgetsData } from 'core-components/sitebuilder/models/IWidgetData'
import { IWidgetData } from 'core-components/sitebuilder/store/models/IWidgetData'
import { selectSitePages } from 'core-components/sitebuilder/store/selectors/selectSitePages'
import { selectWidgetData } from 'core-components/sitebuilder/store/selectors/selectWidgetData'
import { setSitePages, setWidgetData } from 'core-components/sitebuilder/store/siteBuilderSlice'
import { arrayUnique } from 'src/core/classes/helpers/Helper'
import { REDIRECT_SELECT_DATA } from 'src/core/sitebuilder/widgets/sitePagesDetails/constants'
import {
  useCreateSitePageMutation,
  useDeleteSitePageMutation,
  useUpdateBulkSitePagesMutation,
  useUpdateSitePageMutation,
  useUpdateSitePageRolesMutation,
  useUpdateSitePageWidgetsMutation,
} from 'src/core/store/service/sitebuilder/pageApi'
import Logger from 'src/core/utils/Logger'

export const useSitePagesService = () => {
  const [createSitePageApi] = useCreateSitePageMutation()
  const [updateSitePageApi] = useUpdateSitePageMutation()
  const [deleteSitePageApi] = useDeleteSitePageMutation()
  const [updateBulkSitePageApi] = useUpdateBulkSitePagesMutation()
  const [updateSitePageWidgetsApi] = useUpdateSitePageWidgetsMutation()
  const [updateRolesApi] = useUpdateSitePageRolesMutation()

  const dispatch = useDispatch()
  const storeWidgetData = useSelector(selectWidgetData)
  const storeSitePages = useSelector(selectSitePages)

  const selectItem = useCallback(
    (id: string) => {
      dispatch(setWidgetData({ ...storeWidgetData, selectedSitePageId: id }))
    },
    [dispatch, storeWidgetData]
  )

  const setSitePagesToStore = useCallback(
    (newSitePages: ISitePage[], widgetData?: Partial<IWidgetData>) => {
      newSitePages.sort((f, s) => {
        if (f && s) {
          return f.order - s.order
        }
        return 0
      })

      if (widgetData) {
        dispatch(setWidgetData(widgetData))
      }
      dispatch(setSitePages(newSitePages))
    },
    [dispatch]
  )

  const updateChildrenIds = useCallback(
    (parentSitePage: ISitePage, addIds: string[], removeIds: string[] = []): ISitePage => {
      const childrenIds = arrayUnique([
        ...parentSitePage.childrenIds.filter((i) => !removeIds.includes(i)),
        ...addIds,
      ])
      return {
        ...parentSitePage,
        hasChildren: !!childrenIds.length,
        childrenIds,
      }
    },
    []
  )

  const resort = useCallback(
    (items: ISitePage[], item: ISitePage, position: number): ISitePage[] => {
      const isItemAlreadyInItems = !!items.find((i) => i.id === item.id)
      const filteredItems = isItemAlreadyInItems ? items.filter((i) => i.id !== item.id) : items
      const result = [...filteredItems.slice(0, position), item, ...filteredItems.slice(position)]
      return result.map((model, index) => ({ ...model, order: index * 10 }))
    },
    []
  )

  const addItem = useCallback(
    (parentId: string) => {
      const sitePages = storeSitePages.map((el) => {
        if (el.id === parentId) {
          return { ...el, hasChildren: true }
        }
        return el
      })
      const parentSitePage = sitePages.find((i) => i.id === parentId)
      const generatedId = `new-${new Date().getTime()}`
      const newSitePage = createSitePage({
        id: generatedId,
        isNew: true,
        root: parentSitePage ? parentSitePage.root : parentId,
        parent: parentSitePage ? parentId : '#',
        childrenIds: [],
        name: generatedId,
        redirectOption: REDIRECT_SELECT_DATA.INNER,
        defaultSettings: {
          items: [
            {
              isFlexLayout: false,
              size: '2/12',
              widgets: [],
            },
          ],
        },
      })
      setSitePagesToStore([...sitePages, newSitePage], { selectedSitePageId: newSitePage.id })

      return newSitePage
    },
    [setSitePagesToStore, storeSitePages]
  )

  const moveItem = useCallback(
    (id: string, fromParentId: string, toParentId: string, position: number, persist = true) => {
      const movingSitePage = storeSitePages.find((i) => i.id === id)
      if (!movingSitePage) throw new Error('SitePage is not found')

      const fromSitePage = storeSitePages.find((i) => i.id === fromParentId)
      if (!fromSitePage) throw new Error('fromPageParent is not found')

      const toSitePage = storeSitePages.find((i) => i.id === toParentId)
      if (!toSitePage) return

      if (fromParentId === toParentId) {
        let children = getSortedChildren(toSitePage, storeSitePages)
        children = resort(children, movingSitePage, position)
        const childrenIds = children.map((i) => i.id)
        const nextToSitePage: ISitePage = { ...toSitePage, childrenIds }
        const nextSitePages = [
          ...storeSitePages.filter(
            (i) => i.id !== nextToSitePage.id && !childrenIds.includes(i.id)
          ),
          ...children,
          nextToSitePage,
        ]
        setSitePagesToStore(nextSitePages)
        if (persist) {
          updateBulkSitePageApi({ sitePages: children }).catch(Logger.error)
        }
      } else {
        const nextMovingSitePage = { ...movingSitePage, parent: toSitePage.id }
        const nextFromSitePage = updateChildrenIds(fromSitePage, [], [nextMovingSitePage.id])
        let children = getSortedChildren(toSitePage, storeSitePages)
        children = resort(children, nextMovingSitePage, position)
        const childrenIds = children.map((i) => i.id)
        const nextToSitePage = updateChildrenIds(toSitePage, childrenIds, [])

        const nextSitePages = [
          ...storeSitePages.filter(
            (i) => ![...childrenIds, nextToSitePage.id, fromParentId].includes(i.id)
          ),
          ...children,
          nextToSitePage,
          nextFromSitePage,
        ]
        setSitePagesToStore(nextSitePages)
        if (persist) {
          updateBulkSitePageApi({ sitePages: children }).catch(Logger.error)
        }
      }
    },
    [storeSitePages, resort, setSitePagesToStore, updateBulkSitePageApi, updateChildrenIds]
  )

  const deleteItem = useCallback(
    async (id: string): Promise<ISitePage> => {
      const sitePage = storeSitePages.find((i) => i.id === id)
      if (!sitePage) throw new Error('SitePage not found')
      const parentSitePage = storeSitePages.find((i) => i.id === sitePage.parent)
      if (!parentSitePage) throw new Error('Parent SitePage not found')
      const childrenIds = sitePage.childrenIds
      const children = storeSitePages.filter((i) => childrenIds.includes(i.id))
      const removedPages = [sitePage, ...children]

      if (!sitePage.isNew) {
        const deletePromises = removedPages.map((page) => deleteSitePageApi(page.id))

        const resultRaw = await Promise.all(deletePromises)
        const result = resultRaw.map((el) => {
          if ('data' in el) {
            return el.data
          }
          return { success: false, id: null }
        })

        const successResult = result.filter((i) => i.success)
        const errorResult = result.filter((i) => !i.success)
        if (successResult.length) {
          const newSitePages = storeSitePages.filter(
            (el) => !successResult.find((p) => p.id === el.id)
          )
          setSitePagesToStore(newSitePages, { selectedSitePageId: null })
        }
        if (errorResult.length) {
          const errorIds = errorResult.map((i) => i.id).join(', ')
          throw new Error(`Error deleting SitePage with id: ${errorIds}`)
        }
      } else {
        let newSitePages = storeSitePages.filter((el) => el.id !== sitePage.id)

        const parent = storeSitePages.find((i) => i.id === sitePage.parent)
        const newParent = parent ? updateChildrenIds(parent, [], [sitePage.id]) : null
        newSitePages = newSitePages.map((el) => {
          if (el.id === parent?.id) {
            return newParent!
          }
          return el
        })
        setSitePagesToStore(newSitePages, { selectedSitePageId: null })
      }
      return sitePage
    },
    [deleteSitePageApi, setSitePagesToStore, storeSitePages]
  )

  const updatePageWidgetsData = useCallback(
    async (
      sitePage: ISitePage,
      newWidgetsData: IWidgetsData[],
      persist: boolean
    ): Promise<ISitePage> => {
      const widgetsIds = newWidgetsData.map((i) => i.i)
      const availableWidgetsData = newWidgetsData
      const widgetsData = sitePage.widgetsData.filter((i) => widgetsIds.includes(i.i))
      const nextSitePage = { ...sitePage, widgetsData, availableWidgetsData }
      if (storeSitePages) {
        const nextSitePages = storeSitePages.filter((i) => i.id !== nextSitePage.id)
        setSitePagesToStore([...nextSitePages, nextSitePage])
      }
      if (persist) {
        await updateSitePageWidgetsApi({ sitePage: nextSitePage }).catch(Logger.error)
      }
      await updateSitePageApi({ sitePage })
      return nextSitePage
    },
    [setSitePagesToStore, storeSitePages, updateSitePageApi, updateSitePageWidgetsApi]
  )

  const replaceItem = useCallback(
    ({
      oldSitePage,
      newSitePage,
      selectItemId,
      currentSitePages,
    }: {
      oldSitePage: ISitePage | null
      newSitePage: ISitePage
      currentSitePages: ISitePage[]
      selectItemId?: string
    }) => {
      const nextSitePage: ISitePage = { ...newSitePage, id: newSitePage.id }
      let newSitePages = [...currentSitePages]

      if (newSitePage.id !== oldSitePage?.id) {
        newSitePages = currentSitePages.filter((i) => i.id !== oldSitePage?.id)
      }

      newSitePages = newSitePages.map((el) => (el.id === newSitePage.id ? newSitePage : el))
      setSitePagesToStore(newSitePages)

      if (selectItemId) {
        selectItem(selectItemId)
      }

      const wasMoved = newSitePage.parent !== oldSitePage?.parent
      if (wasMoved) {
        moveItem(nextSitePage.id, oldSitePage?.parent || '', newSitePage.parent, 0, false)
      } else {
        if (newSitePage.isNew) {
          const parentSitePage = currentSitePages.find((i) => i.id === newSitePage.parent)
          if (!parentSitePage) {
            throw new Error('Parent SitePage not found')
          }
          const nextParentSitePage = updateChildrenIds(parentSitePage, [newSitePage.id], [])
          const updatedSitePages = currentSitePages.map((el) =>
            el.id !== nextParentSitePage.id ? el : nextParentSitePage
          )
          setSitePagesToStore(updatedSitePages)
        }
      }
    },
    [setSitePagesToStore, selectItem, moveItem, updateChildrenIds]
  )

  const saveItemToServer = useCallback(
    async ({
      oldId,
      oldSitePage,
      newSitePage,
      selectItemId,
    }: {
      oldId: string
      oldSitePage: ISitePage | null
      newSitePage: ISitePage
      selectItemId?: string
    }) => {
      const isNew = newSitePage.isNew
      const result = isNew
        ? await createSitePageApi({ sitePage: newSitePage })
        : await updateSitePageApi({ sitePage: newSitePage })
      if ('data' in result) {
        let newSitePages = [...storeSitePages]
        if (isNew) {
          const nextSitePage = { ...newSitePage, isNew: false }
          newSitePages = [
            ...storeSitePages.filter((i) => i.id !== newSitePage.id && i.id !== oldId),
            nextSitePage,
          ]
          setSitePagesToStore(newSitePages, {
            selectedSitePageId: nextSitePage.id,
          })
        }
        await updateRolesApi(newSitePage)

        replaceItem({
          oldSitePage,
          newSitePage,
          selectItemId,
          currentSitePages: newSitePages,
        })
      } else if ('error' in result) {
        const errorMessage = `Can’t create page ${escapeHTML(newSitePage.name)}. Please try again or contact the administrator.`
        NotificationController.showError({
          message: errorMessage,
        })

        throw new Error(errorMessage)
      }
      return result.data as ISitePage
    },
    [
      createSitePageApi,
      replaceItem,
      setSitePagesToStore,
      storeSitePages,
      updateRolesApi,
      updateSitePageApi,
    ]
  )

  const saveItemsToServer = useCallback(
    async (persistSitePages: ISitePage[]) => {
      const sitePagesToCreate = persistSitePages.filter((i) => i.isNew)
      const sitePagesToUpdate = persistSitePages.filter((i) => !i.isNew)
      const ids: string[] = [
        ...sitePagesToCreate.map((i) => i.id),
        ...sitePagesToUpdate.map((i) => i.id),
      ]
      const otherSitePages = storeSitePages.filter((i) => !ids.includes(i.id))
      setSitePagesToStore([
        ...sitePagesToCreate.map((i) => ({ ...i, isNew: false })),
        ...sitePagesToUpdate,
        ...otherSitePages,
      ])
      sitePagesToCreate.forEach((sitePage) => createSitePageApi({ sitePage }))
      updateBulkSitePageApi({ sitePages: sitePagesToUpdate }).catch(Logger.error)
    },
    [createSitePageApi, setSitePagesToStore, storeSitePages, updateBulkSitePageApi]
  )

  return {
    selectItem,
    addItem,
    deleteItem,
    moveItem,
    updatePageWidgetsData,
    saveItemToServer,
    saveItemsToServer,
  }
}
